import { z, type ZodType } from 'zod';
import { serializedSchemaConfigSchema } from './config';
import { fnDescriptorSchema, zodDescriptorSchema } from './special';
import { EntryType, type EntryItemType } from '@morph-mapper/types';

const nullableOptional = <TSchema extends z.ZodTypeAny>(schema: TSchema) =>
  z.preprocess(
    (value) => (value === null ? undefined : value),
    schema.optional(),
  );

const entryItemTypeValues = Object.values(EntryType).filter(
  (value): value is EntryItemType => value !== EntryType.Map,
) as [EntryItemType, ...EntryItemType[]];

const entryItemTypeSchema = z.enum(entryItemTypeValues);

export type SerializedSchemaMap = {
  displayName?: string;
  outputType?: string;
  configKey?: string;
  values: Record<string, SerializedSchemaNode>;
};

export type SerializedSchemaNode = SerializedSchemaEntry | SerializedSchemaMap;

const serializedSchemaEntrySchema = z.object({
  displayName: z.string().optional(),
  validation: zodDescriptorSchema,
  type: entryItemTypeSchema,
  allowedTypes: z.array(entryItemTypeSchema).optional(),
  prefill: nullableOptional(fnDescriptorSchema),
  configKey: z.string().optional(),
  semantics: z.string().optional(),
  updateObject: z
    .object({
      type: z.string().optional(),
      class: z.string().optional(),
      dropdownid: z.number().optional(),
      fieldtype: z.string().optional(),
      enabled: z.boolean().optional(),
    })
    .optional(),
});

export type SerializedSchemaEntry = z.infer<typeof serializedSchemaEntrySchema>;

const serializedSchemaMapSchema: ZodType<SerializedSchemaMap> = z.lazy(
  (): ZodType<SerializedSchemaMap> =>
    z.object({
      displayName: z.string().optional(),
      outputType: z.string().optional(),
      configKey: z.string().optional(),
      values: z.record(z.string(), serializedSchemaNodeSchema),
    }) as ZodType<SerializedSchemaMap>,
);

const serializedSchemaNodeSchema: ZodType<SerializedSchemaNode> = z.lazy(
  (): ZodType<SerializedSchemaNode> =>
    z.union([
      serializedSchemaEntrySchema,
      serializedSchemaMapSchema,
    ]) as ZodType<SerializedSchemaNode>,
);

export const serializedSchemaSchema = z.object({
  updateTemplateDiscardFields: z.array(z.string()),
  config: serializedSchemaConfigSchema.optional(),
  definition: serializedSchemaMapSchema,
});

export type SerializedSchema = z.infer<typeof serializedSchemaSchema>;
